import { StyleSheet, css } from 'aphrodite';
import type { FC } from 'react';

import { AiButton } from '../../../web/components-v2/AiButton';
import { Button } from '../../../web/components-v2/Button';
import { SPACE, THEME } from '../../../web/components-v2/Theme/theme';
import type { CoachMessage, RecentCallsFeedbackPhase } from '../ai';
import { AIChatPanel } from './AIChatPanel';

export type RecentCallsFeedbackPanelProps = {
	messages: CoachMessage[];
	phase: RecentCallsFeedbackPhase;
	isResponding: boolean;
	followUpFailed?: boolean;
	onStart: () => void;
	onSend: (text: string) => void;
};

export const RecentCallsFeedbackPanel: FC<RecentCallsFeedbackPanelProps> = ({
	messages,
	phase,
	isResponding,
	followUpFailed = false,
	onStart,
	onSend,
}) => {
	return (
		<AIChatPanel
			title='Coaching feedback'
			messages={messages}
			isResponding={isResponding || phase !== 'ready'}
			onSend={onSend}
			onNewConversation={phase === 'idle' ? undefined : onStart}
			newConversationLabel='Start a new review'
			notice={<FeedbackNotice phase={phase} followUpFailed={followUpFailed} onStart={onStart} />}
		/>
	);
};

const FeedbackNotice: FC<{ phase: RecentCallsFeedbackPhase; followUpFailed: boolean; onStart: () => void }> = ({
	phase,
	followUpFailed,
	onStart,
}) => {
	switch (phase) {
		case 'idle':
			return (
				<div className={css(styles.notice)}>
					<p className={css(styles.text)}>Get a coaching review of your recent scored calls.</p>
					<AiButton type='button' size='sm' onClick={onStart} title='Get feedback' />
				</div>
			);
		case 'loading':
			return (
				<div className={css(styles.notice)} role='status'>
					<p className={css(styles.text)}>Reviewing your recent calls…</p>
				</div>
			);
		case 'notEnoughCalls':
			return (
				<div className={css(styles.notice)} role='status'>
					<p className={css(styles.text)}>
						You need at least 3 scored calls for a review. Keep dialing and check back after a few more calls.
					</p>
				</div>
			);
		case 'error':
			return (
				<div className={css(styles.notice)} role='alert'>
					<p className={css(styles.text)}>Couldn't get feedback right now.</p>
					<Button variant='secondary' size='sm' onClick={onStart}>
						Try again
					</Button>
				</div>
			);
		case 'ready':
			return followUpFailed ? (
				<div className={css(styles.notice)} role='alert'>
					<p className={css(styles.text)}>Couldn't get a reply to that. Try asking again.</p>
				</div>
			) : null;
		default:
			return null;
	}
};

const styles = StyleSheet.create({
	notice: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: SPACE[8],
		padding: `${SPACE[12]}px ${SPACE[4]}px`,
	},
	text: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.xs,
		lineHeight: 1.5,
		color: 'var(--ink-muted)',
	},
});
